"use client";

import React from "react";
import { useGlobalModalStack, ResponsiveModal } from "@west-tennessee-consulting/ui";

interface UnifiedModalProps {
  /** Modal ID registered in global modal stack */
  modalId: string;

  /** Modal title */
  title: string;

  /** Optional modal description */
  description?: string;

  /** Modal content */
  children: React.ReactNode;

  /** Modal size */
  size?: "sm" | "md" | "lg" | "xl" | "2xl";

  /** Max height of modal */
  maxHeight?: string;

  /** Custom className */
  className?: string;

  /** Force desktop modal presentation instead of responsive drawer on mobile */
  forceModal?: boolean;
}

const sizeMap = {
  sm: "sm" as const,
  md: "md" as const,
  lg: "lg" as const,
  xl: "xl" as const,
  "2xl": "2xl" as const,
};

/**
 * Unified Modal Wrapper
 *
 * Provides consistent modal rendering using the global modal stack.
 * Handles open/close state automatically.
 * Uses ResponsiveModal for proper z-index stacking with Mantine's Modal.Stack.
 *
 * @example
 * ```tsx
 * <UnifiedModal
 *   modalId="create-tasks"
 *   title="Create Task"
 *   description="Create a new task"
 *   size="lg"
 * >
 *   <TaskForm onSubmit={handleSubmit} />
 * </UnifiedModal>
 * ```
 */
export function UnifiedModal({
  modalId,
  title,
  description,
  children,
  size = "md",
  maxHeight = "80vh",
  className = "",
  forceModal = false,
}: UnifiedModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const isOpen = isModalOpen(modalId);

  return (
    <ResponsiveModal
      globalModalId={modalId}
      title={title}
      description={description}
      maxWidth={sizeMap[size]}
      className={className}
      forceModal={forceModal}
    >
      {children}
    </ResponsiveModal>
  );
}

export default UnifiedModal;
